import { Toast } from 'antd-mobile';
import React, { useEffect, useRef, useState } from 'react';

// 移动端检测函数（含主流设备识别）
export const isMobile = () => {
  const userAgent = navigator.userAgent.toLowerCase();

  // 2025年主流移动端设备正则匹配
  const mobileRegex = /(android|webos|iphone|ipod|blackberry|windows phone)/;
  const tabletRegex = /(ipad|tablet|playbook|silk|kindle)/i;

  // 排除平板设备（按需保留）
  return mobileRegex.test(userAgent) && !tabletRegex.test(userAgent) && window.matchMedia('(pointer:coarse)').matches;
};

export const baseToast = (msg: string, config?: { appFontSize?: string }) => {
  return Toast.show({
    duration: 2000,
    content: React.createElement(
      'span',
      {
        style: {
          fontSize: isMobile() ? config?.appFontSize || '1.2rem' : '0.3rem',
        },
      },
      msg
    ),
  });
};

export const baseLoading = (msg?: string) => {
  return Toast.show({
    duration: 0,
    icon: 'loading',
    content: msg
      ? React.createElement(
          'span',
          {
            style: {
              fontSize: isMobile() ? '1.2rem' : '0.3rem',
            },
          },
          msg
        )
      : undefined,
  });
};

// 复制文本
export function copyToClipboard(textToCopy: string) {
  const tempInput = document.createElement('textarea');
  document.body.appendChild(tempInput);
  tempInput.value = textToCopy;
  tempInput.select();
  document.execCommand('copy');
  document.body.removeChild(tempInput);
  baseToast('复制成功');
}

export function parseUrlParams(url: string): { [key: string]: string } {
  const params: { [key: string]: string } = {};
  const urlParts = url.split('?');

  if (urlParts.length > 1) {
    const queryString = urlParts[1];
    const pairs = queryString.split('&');

    pairs.forEach((pair) => {
      const keyValue = pair.split('=');
      const key = decodeURIComponent(keyValue[0]);
      const value = decodeURIComponent(keyValue[1]);
      params[key] = value;
    });
  }

  return params;
}

/**
 * 跳转并可选写入历史地址到地址栏
 * @param url 需要跳转的地址
 * @param historyUrl 可选历史地址，写入当前地址栏
 */
export function jumpWithHistory(url: string, historyUrl?: string) {
  if (!url) return;
  try {
    if (historyUrl) {
      window.history.replaceState(null, document.title, historyUrl);
    }
  } catch (_) {
    // ignore
  }
  window.location.href = url;
}

export function isAndroidIos() {
  const u = navigator.userAgent;
  const isiOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/); // ios终端
  const isIOS1 = u.includes('iPhone') || u.includes('iOS');
  return isiOS || isIOS1 ? 'ios' : 'android';
}

export function judgeInputWrapper() {
  let abortController: AbortController;
  return () => {
    // 先取消上次的监听
    abortController?.abort?.();
    abortController = new AbortController();
    const { signal } = abortController;
    if (isAndroidIos() === 'ios') {
      window.addEventListener(
        'focusin',
        function () {
          if (document?.activeElement?.tagName === 'INPUT' || document?.activeElement?.tagName === 'TEXTAREA') {
            setTimeout(function () {
              document.documentElement.scrollTop = document.body.scrollHeight;
            }, 0);
          }
        },
        { signal }
      );
    } else {
      let resizeTimeout: any = null;
      let hasTriggered = false;

      const triggerScrollIntoView = () => {
        hasTriggered = true;
        if (document?.activeElement?.tagName === 'INPUT' || document?.activeElement?.tagName === 'TEXTAREA') {
          setTimeout(function () {
            document?.activeElement?.scrollIntoView({
              block: 'center',
            });
            abortController?.abort?.();
          }, 0);
        }
      };

      // 设置2秒后手动触发的定时器
      const setManualTrigger = () => {
        if (resizeTimeout) {
          clearTimeout(resizeTimeout);
        }
        resizeTimeout = setTimeout(() => {
          if (!hasTriggered) {
            triggerScrollIntoView();
          }
        }, 300);
      };

      // 初始设置手动触发
      setManualTrigger();

      window.addEventListener(
        'resize',
        function () {
          if (resizeTimeout) {
            clearTimeout(resizeTimeout);
            resizeTimeout = null;
          }
          triggerScrollIntoView();
        },
        { signal }
      );
    }
  };
}

interface Options {
  delay?: number;
  alert?: boolean;
}

/**
 * 创建类型安全的防重复点击函数
 * @param fn 原始点击处理函数
 * @param options 配置项
 */
export function createSafeClick<T extends any[], R>(
  fn: (this: ThisParameterType<(...args: T) => R>, ...args: T) => R,
  options: Options = {}
): (this: ThisParameterType<(...args: T) => R>, ...args: T) => Promise<R | void> {
  let isLocked = false;
  const { delay = 500, alert = false } = options;

  return async function (this: ThisParameterType<(...args: T) => R>, ...args: T): Promise<R | void> {
    if (isLocked) {
      if (alert) console.warn('Operation in progress');
      return;
    }

    try {
      isLocked = true;
      // eslint-disable-next-line consistent-return
      return fn.apply(this, args);
    } finally {
      setTimeout(() => {
        isLocked = false;
      }, delay);
      // eslint-disable-next-line consistent-return,no-unsafe-finally
      return Promise.reject();
    }
  };
}

export const useFormWatchScroll = (className: string) => {
  const [isViewForm, setIsViewForm] = useState<boolean>(true);
  const changeViewTimer = useRef<any>();

  const initObserver = () => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (changeViewTimer.current) {
            clearTimeout(changeViewTimer.current);
          }
          const isView = entry.isIntersecting;
          if (isView) {
            setIsViewForm(entry.isIntersecting);
          } else {
            changeViewTimer.current = setTimeout(() => {
              setIsViewForm(entry.isIntersecting);
            }, 400);
          }
        });
      },
      {
        root: null, // 默认视口为根
        rootMargin: '0px', // 可扩展检测边界
        threshold: 0.3, // 元素可见比例触发阈值
      }
    );

    const oDom = document.querySelector(className) as any;
    if (oDom) {
      observer.observe(oDom);
    } else {
      setTimeout(initObserver, 200);
    }
  };

  useEffect(() => {
    setTimeout(initObserver, 200);
  }, []);

  return {
    isViewForm,
  };
};

/**
 * 校验LINE账号ID合法性
 * @param {string} id - 待校验的ID
 * @returns {boolean} - 合法返回true，否则false
 */
export const validateLineId = (id: string) => {
  // 正则规则：允许字母/数字/_-，长度4-20
  const reg = /^[a-zA-Z0-9_-]{4,20}$/;
  return reg.test(id);
};

export function withBaseLoading<T extends (...args: any[]) => any>(fn: T, msg?: string) {
  return async (...args: Parameters<T>): Promise<Awaited<ReturnType<T>>> => {
    const handler = baseLoading(msg);
    try {
      return await fn(...args);
    } finally {
      try {
        handler?.close?.();
      } catch (_) {
        Toast.clear();
      }
    }
  };
}
